<script setup lang="ts">
import { computed } from 'vue'
import { Loader2 } from 'lucide-vue-next'
import { cva } from 'class-variance-authority'
import { cn } from '@/lib/utils'

type Variant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'destructive'
type Size = 'sm' | 'md' | 'lg'

const props = defineProps<{
  variant?: Variant
  size?: Size
  disabled?: boolean
  loading?: boolean
}>()

const emit = defineEmits<{ click: [event: MouseEvent] }>()

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-sm border font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'border-primary bg-primary text-white hover:border-primary-dark hover:bg-primary-dark focus-visible:ring-primary',
        secondary: 'border-[color:var(--color-border-strong)] bg-white text-[var(--color-ink)] hover:bg-[var(--color-surface-muted)] focus-visible:ring-gray-300',
        outline: 'border-[color:var(--color-border-strong)] bg-transparent text-[var(--color-ink)] hover:bg-white focus-visible:ring-gray-300',
        ghost: 'border-transparent bg-transparent text-[var(--color-ink-soft)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-ink)]',
        destructive: 'border-error bg-error text-white hover:border-red-600 hover:bg-red-600 focus-visible:ring-error',
      },
      size: {
        sm: 'h-7 px-2.5 text-xs',
        md: 'h-9 px-3 text-sm',
        lg: 'h-10 px-4 text-sm',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
)

const classes = computed(() => buttonVariants({ variant: props.variant, size: props.size }))

function handleClick(event: MouseEvent) {
  emit('click', event)
}
</script>

<template>
  <button
    :class="classes"
    :disabled="disabled || loading"
    @click="handleClick"
    v-bind="$attrs"
  >
    <Loader2 v-if="loading" class="h-4 w-4 animate-spin" />
    <slot />
  </button>
</template>
